"use client";

import { FormColumns, FormLabel } from "components/form";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from "ui/components/form";
import { RadioGroup, RadioGroupItem } from "ui/components/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "ui/components/select";
import { useNavigate, useParams } from "react-router-dom";

import AmountWithCurrencyInput from "components/form/amount-currency";
import ButtonWithLoading from "@/components/button-with-loading";
import { Form } from "react-crud-form";
import { Input } from "ui/components/input";
import { Label } from "ui/components/label";
import { recurringJobConfigSchema } from "@/modules/work/jobs/config/recurring/schema";
import { useCreateRecurringConfig, useUpdateRecurringConfig } from "@/modules/work/jobs/config/recurring/mutation";
import { useForm } from "react-hook-form";
import { useHotkeys } from "react-hotkeys-hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { PermissionControlledInput } from "permissions/components/input";
import { FormActions } from "components/form/actions";

const RecurringConfigForm = ({ config }) => {
  const navigate = useNavigate();

  const { jobId } = useParams();
  const form = useForm({
    resolver: zodResolver(recurringJobConfigSchema),
    defaultValues: config || {
      billing_frequency: "weekly",
    },
  });
  const createMutation = useCreateRecurringConfig();
  const updateMutation = useUpdateRecurringConfig();

  const handleCreate = async (context) => {
    const { values } = context;
    const response = await createMutation.mutateAsync({
      jobId,
      values,
    });
    navigate(-1); // optional
  };

  const handleUpdate = async (id, context) => {
    const { dirtyValues } = context;
    await updateMutation.mutateAsync({
      id,
      values: dirtyValues,
    });
    navigate(-1);
  };

  const billingFrequency = form.watch("billing_frequency");

  const weekdays = [
    { label: "Monday", value: 1 },
    { label: "Tuesday", value: 2 },
    { label: "Wednesday", value: 3 },
    { label: "Thursday", value: 4 },
    { label: "Friday", value: 5 },
    { label: "Saturday", value: 6 },
    { label: "Sunday", value: 0 },
  ];

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  return (
    <Form
      form={form}
      create={handleCreate}
      update={handleUpdate}
      itemId={config?.id || "+"}
    >
      {({ isCreating, createItem, isUpdating, updateItem, isDirty, isNewItem }) => {
        useHotkeys(
          "ctrl+s, cmd+s",
          (event) => {
            event.preventDefault();
            if (isNewItem) {
              createItem();
            } else {
              updateItem();
            }
          },
          { preventDefault: true },
        );
        return (
          <div className="p-6 max-w-md space-y-6">
            <AmountWithCurrencyInput
              form={form}
              amountKey="amount"
              amountLabel="Amount"
              collection="recurring_job_config"
              isNewItem={isNewItem}
            />


            <FormField
              control={form.control}
              name="billing_frequency"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={isNewItem ? "create:SC,AM" : "update:SC,AM"}
                    collection="recurring_job_config"
                    field={field.name}
                    type="form"
                  >
                    <FormLabel required={true}>Billing Frequency</FormLabel>

                    <RadioGroup
                      onValueChange={field.onChange}
                      value={field.value}
                      className="flex gap-6"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="weekly" id="weekly" />
                        <Label htmlFor="weekly">Weekly</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="monthly" id="monthly" />
                        <Label htmlFor="monthly">Monthly</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="yearly" id="yearly" />
                        <Label htmlFor="yearly">Yearly</Label>
                      </div>
                    </RadioGroup>
                  </PermissionControlledInput>
                  <FormDescription>
                    Choose how frequently recurring charges are billed (weekly, monthly, or yearly).
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            {billingFrequency === "weekly" && (
              <FormField
                control={form.control}
                name="billing_by_week_day"
                render={({ field }) => (
                  <FormItem>
                    <PermissionControlledInput
                      rule={isNewItem ? "create:SC,AM" : "update:SC,AM"}
                      collection="recurring_job_config"
                      field={field.name}
                      type="form"
                    >
                      <FormLabel required={true}>Billing by Weekday</FormLabel>
                      <Select
                        onValueChange={(val) => field.onChange(Number(val))}
                        value={field.value?.toString()}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select a weekday" />
                        </SelectTrigger>

                        <SelectContent>
                          {weekdays.map((day) => (
                            <SelectItem
                              key={day.value}
                              value={day.value.toString()}
                            >
                              {day.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </PermissionControlledInput>
                    <FormDescription>
                      Select the day of the week when weekly recurring billing occurs.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {billingFrequency === "monthly" && (
              <FormField
                control={form.control}
                name="billing_by_month_date"
                render={({ field }) => (
                  <FormItem>
                    <PermissionControlledInput
                      rule={isNewItem ? "create:SC,AM" : "update:SC,AM"}
                      collection="recurring_job_config"
                      field={field.name}
                      type="form"
                    >
                      <FormLabel required={true}>Billing by Month Date (1-31)</FormLabel>
                      <Select
                        onValueChange={(value) => field.onChange(Number(value))}
                        value={field.value?.toString()}
                      >
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select a date" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {Array.from({ length: 31 }, (_, i) => i + 1).map(
                            (date) => (
                              <SelectItem key={date} value={date.toString()}>
                                {date}
                              </SelectItem>
                            ),
                          )}
                        </SelectContent>
                      </Select>
                    </PermissionControlledInput>
                    <FormDescription>
                      Select the date of the month when monthly recurring billing occurs.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {billingFrequency === "yearly" && (
              <FormField
                control={form.control}
                name="billing_by_month"
                render={({ field }) => (
                  <FormItem>
                    <PermissionControlledInput
                      rule={isNewItem ? "create:SC,AM" : "update:SC,AM"}
                      collection="recurring_job_config"
                      field={field.name}
                      type="form"
                    >
                      <FormLabel required={true}>Billing by Month</FormLabel>
                      <Select
                        onValueChange={(val) => field.onChange(Number(val))}
                        value={field.value?.toString()}
                      >
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select a month" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {months.map((month, index) => (
                            <SelectItem key={month} value={index.toString()}>
                              {month}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </PermissionControlledInput>
                    <FormDescription>
                      Select the month of the year when yearly recurring billing occurs.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            <FormField
              control={form.control}
              name="billing_interval"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={isNewItem ? "create:SC,AM" : "update:SC,AM"}
                    collection="recurring_job_config"
                    field={field.name}
                    type="form"
                  >
                    <FormLabel required={true}>Billing Interval</FormLabel>

                    <Input
                      type="number"
                      min="1"
                      {...field}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    />
                  </PermissionControlledInput>
                  <FormDescription>
                    Specify the recurring cycle interval (e.g. enter 1 for every cycle, 2 for every 2nd cycle).
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormActions
              rule={isNewItem ? "create:SC,AM" : "update:SC,AM"}
              collection="recurring_job_config"
              isNewItem={isNewItem}
              isCreating={isCreating}
              isUpdating={isUpdating}
              createItem={createItem}
              updateItem={updateItem}
              isDirty={isDirty}
            />
          </div>
        );
      }}
    </Form>
  );
};

export default RecurringConfigForm;
